import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const PageBuilder = () => {
    const [pages, setPages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isEditing, setIsEditing] = useState(false);
    const [currentPage, setCurrentPage] = useState(null);

    useEffect(() => {
        fetchPages();
    }, []);

    const fetchPages = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/custom-pages');
            setPages(res.data);
        } catch (error) {
            console.error("Error fetching custom pages", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateNew = () => {
        setCurrentPage({
            title: '',
            slug: '',
            status: 'active',
            components: []
        });
        setIsEditing(true);
    };

    const handleEdit = async (page) => {
        try {
            const res = await api.get(`/custom-pages/${page.id}`);
            setCurrentPage(res.data);
            setIsEditing(true);
        } catch (error) {
            console.error("Error fetching page details", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Seguro que deseas eliminar esta página?")) {
            try {
                await api.delete(`/custom-pages/${id}`);
                fetchPages();
            } catch (error) {
                console.error("Error deleting page", error);
            }
        }
    };

    const handleSave = async () => {
        if (!currentPage.title || !currentPage.slug) {
            alert("El título y el slug son obligatorios.");
            return;
        }
        try {
            if (currentPage.id) {
                await api.put(`/custom-pages/${currentPage.id}`, currentPage);
            } else {
                await api.post('/custom-pages', currentPage);
            }
            setIsEditing(false);
            fetchPages();
        } catch (error) {
            console.error("Error saving page", error);
            alert(error.response?.data?.message || "Error al guardar la página");
        }
    };

    const addComponent = (type) => {
        const newComponent = { id: Date.now().toString(), type, props: {} };
        switch (type) {
            case 'TextBlock':
                newComponent.props = { content: 'Nuevo texto...', color: '#ffffff', align: 'left', fontSize: '16px', fontWeight: 'normal' };
                break;
            case 'Image':
                newComponent.props = { url: '', alt: '', width: '100%', borderRadius: '0px' };
                break;
            case 'Video':
                newComponent.props = { url: 'https://www.youtube.com/embed/...', title: 'Video' };
                break;
            case 'Button':
                newComponent.props = { text: 'Click Aquí', link: '#', bgColor: '#4f46e5', textColor: '#ffffff' };
                break;
            case 'Spacer':
                newComponent.props = { height: '50px' };
                break;
            default:
                break;
        }
        setCurrentPage({ ...currentPage, components: [...currentPage.components, newComponent] });
    };

    const updateComponent = (id, newProps) => {
        setCurrentPage({
            ...currentPage,
            components: currentPage.components.map(c => c.id === id ? { ...c, props: { ...c.props, ...newProps } } : c)
        });
    };

    const moveComponent = (index, direction) => {
        const newComponents = [...currentPage.components];
        if (direction === 'up' && index > 0) {
            [newComponents[index - 1], newComponents[index]] = [newComponents[index], newComponents[index - 1]];
        } else if (direction === 'down' && index < newComponents.length - 1) {
            [newComponents[index + 1], newComponents[index]] = [newComponents[index], newComponents[index + 1]];
        }
        setCurrentPage({ ...currentPage, components: newComponents });
    };

    const removeComponent = (id) => {
        setCurrentPage({
            ...currentPage,
            components: currentPage.components.filter(c => c.id !== id)
        });
    };

    // Editor View
    if (isEditing && currentPage) {
        return (
            <div className="animate-fadeIn max-w-7xl mx-auto">
                <header className="flex justify-between items-center mb-8 bg-white p-6 rounded-[30px] shadow-sm">
                    <div>
                        <h1 className="text-3xl font-extrabold text-slate-900">{currentPage.id ? 'Editar Página' : 'Nueva Página'}</h1>
                    </div>
                    <div className="flex space-x-4">
                        <button onClick={() => setIsEditing(false)} className="px-6 py-2 text-slate-500 font-bold hover:bg-slate-100 rounded-xl transition-all">Cancelar</button>
                        <button onClick={handleSave} className="px-8 py-2 bg-indigo-600 text-white font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-lg">Guardar</button>
                    </div>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Controls (Left) */}
                    <div className="lg:col-span-1 space-y-6">
                        <div className="bg-white p-6 rounded-[30px] shadow-sm space-y-4">
                            <h2 className="text-xl font-bold text-slate-800 border-b pb-2">Configuración General</h2>
                            <div>
                                <label className="block text-sm font-bold text-slate-600 mb-1">Título de la página</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={currentPage.title}
                                    onChange={e => setCurrentPage({...currentPage, title: e.target.value})}
                                    placeholder="Ej: Instrucciones Evento"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-600 mb-1">Slug (URL)</label>
                                <input 
                                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={currentPage.slug}
                                    onChange={e => setCurrentPage({...currentPage, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-')})}
                                    placeholder="ej: instrucciones-evento"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-bold text-slate-600 mb-1">Estado</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500"
                                    value={currentPage.status}
                                    onChange={e => setCurrentPage({...currentPage, status: e.target.value})}
                                >
                                    <option value="active">Activa</option>
                                    <option value="inactive">Inactiva</option>
                                </select>
                            </div>
                        </div>

                        <div className="bg-white p-6 rounded-[30px] shadow-sm space-y-4 sticky top-6">
                            <h2 className="text-xl font-bold text-slate-800 border-b pb-2">Agregar Componente</h2>
                            <div className="grid grid-cols-2 gap-3">
                                <button onClick={() => addComponent('TextBlock')} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-all text-sm font-bold text-slate-700 flex flex-col items-center gap-2"><span>📝</span> Texto</button>
                                <button onClick={() => addComponent('Image')} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-all text-sm font-bold text-slate-700 flex flex-col items-center gap-2"><span>🖼️</span> Imagen</button>
                                <button onClick={() => addComponent('Video')} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-all text-sm font-bold text-slate-700 flex flex-col items-center gap-2"><span>▶️</span> Video</button>
                                <button onClick={() => addComponent('Button')} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-all text-sm font-bold text-slate-700 flex flex-col items-center gap-2"><span>👆</span> Botón</button>
                                <button onClick={() => addComponent('Spacer')} className="p-3 bg-slate-50 border border-slate-200 rounded-xl hover:bg-indigo-50 hover:border-indigo-200 transition-all text-sm font-bold text-slate-700 flex flex-col items-center gap-2 col-span-2"><span>↕️</span> Espaciador</button>
                            </div>
                        </div>
                    </div>

                    {/* Editor (Middle) */}
                    <div className="lg:col-span-2 space-y-4">
                        <h2 className="text-xl font-bold text-slate-800">Constructor de Componentes</h2>
                        {currentPage.components.length === 0 ? (
                            <div className="bg-white/50 border-2 border-dashed border-slate-300 rounded-[30px] p-20 text-center text-slate-400 font-bold">
                                No hay componentes. Selecciona uno del menú lateral.
                            </div>
                        ) : (
                            currentPage.components.map((comp, idx) => (
                                <div key={comp.id} className="bg-white p-6 rounded-[20px] shadow-sm border border-slate-200 relative group">
                                    <div className="absolute -left-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button onClick={() => moveComponent(idx, 'up')} className="bg-slate-800 text-white p-2 rounded-full hover:bg-indigo-600 shadow-lg text-xs" disabled={idx === 0}>↑</button>
                                        <button onClick={() => moveComponent(idx, 'down')} className="bg-slate-800 text-white p-2 rounded-full hover:bg-indigo-600 shadow-lg text-xs" disabled={idx === currentPage.components.length - 1}>↓</button>
                                    </div>
                                    <div className="flex justify-between items-center mb-4 border-b pb-2">
                                        <h3 className="font-bold text-indigo-800 uppercase tracking-widest text-xs">{comp.type}</h3>
                                        <button onClick={() => removeComponent(comp.id)} className="text-red-500 hover:text-red-700 text-sm font-bold">✕ Eliminar</button>
                                    </div>

                                    {comp.type === 'TextBlock' && (
                                        <div className="space-y-3">
                                            <textarea 
                                                className="w-full bg-slate-50 border border-slate-200 p-3 rounded-xl focus:ring-2 focus:ring-indigo-500 min-h-[100px]"
                                                value={comp.props.content}
                                                onChange={e => updateComponent(comp.id, { content: e.target.value })}
                                                placeholder="Contenido (acepta HTML básico)"
                                            />
                                            <div className="grid grid-cols-4 gap-2">
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Color (Hex)</label>
                                                    <input type="text" value={comp.props.color} onChange={e => updateComponent(comp.id, { color: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Alineación</label>
                                                    <select value={comp.props.align} onChange={e => updateComponent(comp.id, { align: e.target.value })} className="w-full border rounded-lg p-2 text-sm">
                                                        <option value="left">Izquierda</option>
                                                        <option value="center">Centro</option>
                                                        <option value="right">Derecha</option>
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Tamaño</label>
                                                    <input type="text" value={comp.props.fontSize} onChange={e => updateComponent(comp.id, { fontSize: e.target.value })} placeholder="16px" className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Grosor</label>
                                                    <select value={comp.props.fontWeight} onChange={e => updateComponent(comp.id, { fontWeight: e.target.value })} className="w-full border rounded-lg p-2 text-sm">
                                                        <option value="normal">Normal</option>
                                                        <option value="bold">Negrita (Bold)</option>
                                                        <option value="black">Muy Gruesa (Black)</option>
                                                    </select>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {comp.type === 'Image' && (
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">URL de la Imagen</label>
                                                <input type="text" value={comp.props.url} onChange={e => updateComponent(comp.id, { url: e.target.value })} placeholder="https://..." className="w-full border rounded-lg p-2 text-sm" />
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Ancho</label>
                                                    <input type="text" value={comp.props.width} onChange={e => updateComponent(comp.id, { width: e.target.value })} placeholder="100% o 300px" className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Borde Redondeado</label>
                                                    <input type="text" value={comp.props.borderRadius} onChange={e => updateComponent(comp.id, { borderRadius: e.target.value })} placeholder="0px o 20px" className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {comp.type === 'Video' && (
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">URL del iframe (Ej: YouTube Embed)</label>
                                                <input type="text" value={comp.props.url} onChange={e => updateComponent(comp.id, { url: e.target.value })} placeholder="https://www.youtube.com/embed/..." className="w-full border rounded-lg p-2 text-sm" />
                                            </div>
                                        </div>
                                    )}

                                    {comp.type === 'Button' && (
                                        <div className="space-y-3">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Texto del Botón</label>
                                                    <input type="text" value={comp.props.text} onChange={e => updateComponent(comp.id, { text: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Enlace (URL o /ruta)</label>
                                                    <input type="text" value={comp.props.link} onChange={e => updateComponent(comp.id, { link: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Color Fondo (Hex)</label>
                                                    <input type="text" value={comp.props.bgColor} onChange={e => updateComponent(comp.id, { bgColor: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                                <div>
                                                    <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Color Texto (Hex)</label>
                                                    <input type="text" value={comp.props.textColor} onChange={e => updateComponent(comp.id, { textColor: e.target.value })} className="w-full border rounded-lg p-2 text-sm" />
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {comp.type === 'Spacer' && (
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-[10px] uppercase font-bold text-slate-500 mb-1">Altura (px, rem, vh)</label>
                                                <input type="text" value={comp.props.height} onChange={e => updateComponent(comp.id, { height: e.target.value })} placeholder="50px" className="w-full border rounded-lg p-2 text-sm" />
                                            </div>
                                        </div>
                                    )}

                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        );
    }

    // List View
    return (
        <div className="animate-fadeIn">
            <header className="flex justify-between items-center mb-10">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Creador de Páginas</h1>
                    <p className="text-slate-500 mt-2">Crea y gestiona pantallas personalizadas (CMS).</p>
                </div>
                <button 
                    onClick={handleCreateNew}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-indigo-900/20 hover:bg-indigo-500 transition-all flex items-center transform hover:scale-105 active:scale-95"
                >
                    <span className="mr-2 text-xl">+</span> Nueva Página
                </button>
            </header>

            {isLoading ? (
                <div className="text-center py-20 text-slate-400">Cargando páginas...</div>
            ) : (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Título / Slug</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-center">Estado</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {pages.length === 0 ? (
                                <tr><td colSpan="3" className="p-10 text-center text-slate-400 font-bold">No hay páginas creadas.</td></tr>
                            ) : pages.map((page) => (
                                <tr key={page.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="p-6">
                                        <div className="font-bold text-slate-900 text-lg">{page.title}</div>
                                        <div className="text-sm font-medium text-indigo-500 mt-1">/p/{page.slug}</div>
                                    </td>
                                    <td className="p-6 text-center">
                                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${page.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                            {page.status}
                                        </span>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end space-x-2 opacity-1 group-hover:opacity-100 transition-opacity">
                                            <a 
                                                href={`http://localhost:3000/p/${page.slug}`} 
                                                target="_blank" 
                                                rel="noreferrer"
                                                className="p-2.5 rounded-xl bg-blue-50 text-blue-500 hover:bg-blue-100 transition-all shadow-sm flex items-center justify-center"
                                                title="Ver"
                                            >
                                                👁️
                                            </a>
                                            <button 
                                                onClick={() => handleEdit(page)}
                                                className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-600 transition-all shadow-sm"
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(page.id)}
                                                className="p-2.5 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-all shadow-sm"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
};

export default PageBuilder;
